import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const BG = "#F4EBD8";
const TEXT = "#2A1D14";
const MUTED = "#5B4326";
const ACCENT = "#A67B4A";
const EYEBROW = "#8A6A46";
const CROP_ASPECT = "4/5";
const FRAME_LABEL = "Фото знания · 4:5";

const MODES = [
  { key: "table", label: "Таблица" },
  { key: "comparison", label: "Сравнение" },
  { key: "scheme", label: "Схема" },
  { key: "scale", label: "Шкала" },
  { key: "matrix", label: "Матрица" },
  { key: "glossary", label: "Справочник" },
  { key: "faq", label: "FAQ / гармошка" },
];

let idSeq = 0;
function newId(prefix) {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
}

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    caption: raw.caption || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

function blankTable() {
  return {
    columns: [
      { id: newId("col"), title: "Параметр" },
      { id: newId("col"), title: "Значение" },
    ],
    rows: [
      { id: newId("row"), cells: ["", ""] },
      { id: newId("row"), cells: ["", ""] },
    ],
  };
}

function normalizeTable(raw) {
  if (!raw || typeof raw !== "object") return blankTable();
  const rawCols = Array.isArray(raw.columns)
    ? raw.columns
    : Array.isArray(raw.headers)
      ? raw.headers.map((title) => ({ title }))
      : [];
  const columns = rawCols.length
    ? rawCols.map((col, i) => ({
        id: col?.id || `col-legacy-${i}`,
        title: typeof col === "string" ? col : (col?.title || ""),
      }))
    : blankTable().columns;

  const rawRows = Array.isArray(raw.rows) ? raw.rows : [];
  const rows = rawRows.length
    ? rawRows.map((row, i) => {
        const cells = Array.isArray(row)
          ? row
          : Array.isArray(row?.cells)
            ? row.cells
            : columns.map((col) => row?.[col.title] ?? "");
        return {
          id: row?.id || `row-legacy-${i}`,
          cells: columns.map((_, idx) => String(cells[idx] ?? "")),
        };
      })
    : [{ id: newId("row"), cells: columns.map(() => "") }];

  return { columns, rows };
}

function normalizeItems(raw, prefix, fields) {
  if (!Array.isArray(raw) || !raw.length) {
    return [{ id: newId(prefix), ...Object.fromEntries(fields.map((f) => [f, ""])) }];
  }
  return raw.map((item, i) => ({
    id: item?.id || `${prefix}-legacy-${i}`,
    ...Object.fromEntries(fields.map((f) => [f, item?.[f] || ""])),
  }));
}

function deriveInitial(content) {
  const c = content || {};
  const fromExtra = c.extra && typeof c.extra === "object" && c.extra.knowledge
    ? c.extra.knowledge
    : {};
  const k = fromExtra && typeof fromExtra === "object" ? fromExtra : {};

  return {
    eyebrow: k.eyebrow ?? c.eyebrow ?? "Знание",
    title: k.title ?? c.headline ?? "",
    subtitle: k.subtitle ?? c.lead ?? "",
    mode: MODES.some((m) => m.key === k.mode) ? k.mode : "table",
    table: normalizeTable(k.table || c.table),
    comparison: normalizeItems(k.comparison?.items, "cmp", ["title", "text"]),
    scheme: normalizeItems(k.scheme?.items, "sch", ["title", "text"]),
    scale: normalizeItems(k.scale?.items, "scl", ["label", "text"]),
    matrix: {
      columns: Array.isArray(k.matrix?.columns) && k.matrix.columns.length
        ? k.matrix.columns.map((x, i) => ({ id: x?.id || `mx-c-${i}`, title: typeof x === "string" ? x : (x?.title || "") }))
        : [{ id: newId("mx-c"), title: "Причина" }, { id: newId("mx-c"), title: "Решение" }],
      rows: Array.isArray(k.matrix?.rows) && k.matrix.rows.length
        ? k.matrix.rows.map((row, i) => ({
            id: row?.id || `mx-r-${i}`,
            label: row?.label || "",
            cells: Array.isArray(row?.cells) ? row.cells.map((x) => String(x ?? "")) : [],
          }))
        : [{ id: newId("mx-r"), label: "", cells: ["", ""] }],
    },
    glossary: normalizeItems(k.glossary?.items, "gl", ["term", "text"]),
    faq: normalizeItems(k.faq?.items, "faq", ["question", "answer"]),
    image: normalizeImage(k.image),
  };
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: EYEBROW }}>
        {eyebrow || "Знание"}
      </div>
      {title && <h2 className="mt-3 font-serif text-[34px] leading-[1.08]" style={{ color: TEXT }}>{title}</h2>}
      {subtitle && <p className="mt-3 max-w-[62ch] font-serif text-[16px] italic leading-[1.6]" style={{ color: MUTED }}>{subtitle}</p>}
      <div className="my-6 h-px w-16" style={{ backgroundColor: ACCENT }} />
    </div>
  );
}

function TablePreview({ table, mobile }) {
  const columns = table.columns.filter((c) => c.title.trim() || table.rows.some((r) => (r.cells || [])[table.columns.indexOf(c)]?.trim()));
  const indices = columns.map((c) => table.columns.indexOf(c));
  if (!columns.length) return null;

  if (mobile) {
    return (
      <div className="space-y-3">
        {table.rows.map((row) => (
          <div key={row.id} className="border-b pb-3" style={{ borderColor: "#D8C7AA" }}>
            {indices.map((idx) => {
              const value = row.cells[idx] || "";
              if (!value.trim() && !table.columns[idx]?.title.trim()) return null;
              return (
                <div key={table.columns[idx].id} className="grid grid-cols-[100px_1fr] gap-3 py-1.5">
                  <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: EYEBROW }}>{table.columns[idx].title}</div>
                  <div className="font-serif text-[14px] leading-[1.45]" style={{ color: TEXT }}>{value}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border-y" style={{ borderColor: "#D8C7AA" }}>
      <table className="w-full border-collapse text-left">
        <thead>
          <tr>
            {indices.map((idx) => (
              <th key={table.columns[idx].id} className="border-b px-3 py-3 text-[10px] uppercase tracking-[0.18em]"
                style={{ borderColor: "#D8C7AA", color: EYEBROW }}>
                {table.columns[idx].title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => (
            <tr key={row.id} className="border-b last:border-b-0" style={{ borderColor: "#E3D6C0" }}>
              {indices.map((idx) => (
                <td key={table.columns[idx].id} className="px-3 py-3 align-top font-serif text-[15px] leading-[1.5]" style={{ color: TEXT }}>
                  {row.cells[idx] || ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComparisonPreview({ items, mobile }) {
  const filled = items.filter((x) => x.title.trim() || x.text.trim());
  if (!filled.length) return null;
  return (
    <div className={mobile ? "space-y-4" : "grid gap-4 sm:grid-cols-2 lg:grid-cols-3"}>
      {filled.map((item) => (
        <div key={item.id} className="border-t pt-4" style={{ borderColor: ACCENT }}>
          <div className="font-serif text-[20px]" style={{ color: TEXT }}>{item.title}</div>
          <div className="mt-2 whitespace-pre-wrap font-serif text-[14px] leading-[1.6]" style={{ color: MUTED }}>{item.text}</div>
        </div>
      ))}
    </div>
  );
}

function SchemePreview({ items, mobile }) {
  const filled = items.filter((x) => x.title.trim() || x.text.trim());
  if (!filled.length) return null;
  return (
    <div className="space-y-3">
      {filled.map((item, i) => (
        <div key={item.id} className="flex items-start gap-4">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-serif text-[13px]"
            style={{ borderColor: ACCENT, color: TEXT }}>{i + 1}</div>
          <div className="min-w-0 flex-1 pb-3">
            <div className="font-serif text-[18px]" style={{ color: TEXT }}>{item.title}</div>
            {item.text && <div className="mt-1 font-serif text-[14px] leading-[1.55]" style={{ color: MUTED }}>{item.text}</div>}
          </div>
          {!mobile && i < filled.length - 1 && <div className="pt-1 text-[20px]" style={{ color: ACCENT }}>→</div>}
        </div>
      ))}
    </div>
  );
}

function ScalePreview({ items }) {
  const filled = items.filter((x) => x.label.trim() || x.text.trim());
  if (!filled.length) return null;
  return (
    <div>
      <div className="h-px w-full" style={{ backgroundColor: ACCENT }} />
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(filled.length, 4)}, minmax(0,1fr))` }}>
        {filled.map((item) => (
          <div key={item.id} className="relative pt-4">
            <div className="absolute -top-1.5 left-0 h-3 w-3 rounded-full" style={{ backgroundColor: ACCENT }} />
            <div className="font-serif text-[16px]" style={{ color: TEXT }}>{item.label}</div>
            <div className="mt-1 font-serif text-[13px] leading-[1.5]" style={{ color: MUTED }}>{item.text}</div>
          </div>
        ))}
      </div>

    </div>
  );
}

function MatrixPreview({ matrix, mobile }) {
  const cols = matrix.columns;
  if (mobile) {
    return (
      <div className="space-y-4">
        {matrix.rows.map((row) => (
          <div key={row.id} className="border-t pt-3" style={{ borderColor: ACCENT }}>
            {row.label && <div className="font-serif text-[18px]" style={{ color: TEXT }}>{row.label}</div>}
            {cols.map((col, idx) => (
              <div key={col.id} className="mt-2 grid grid-cols-[90px_1fr] gap-3">
                <div className="text-[9px] uppercase tracking-[0.16em]" style={{ color: EYEBROW }}>{col.title}</div>
                <div className="font-serif text-[14px] leading-[1.5]" style={{ color: TEXT }}>{row.cells[idx] || ""}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="border-b px-3 py-3" style={{ borderColor: "#D8C7AA" }} />
            {cols.map((col) => (
              <th key={col.id} className="border-b px-3 py-3 text-left text-[10px] uppercase tracking-[0.18em]"
                style={{ borderColor: "#D8C7AA", color: EYEBROW }}>{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((row) => (
            <tr key={row.id}>
              <th className="border-b px-3 py-3 text-left font-serif text-[15px] font-normal" style={{ borderColor: "#E3D6C0", color: TEXT }}>
                {row.label}
              </th>
              {cols.map((col, idx) => (
                <td key={col.id} className="border-b px-3 py-3 align-top font-serif text-[14px] leading-[1.5]"
                  style={{ borderColor: "#E3D6C0", color: TEXT }}>{row.cells[idx] || ""}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GlossaryPreview({ items }) {
  const filled = items.filter((x) => x.term.trim() || x.text.trim());
  if (!filled.length) return null;
  return (
    <div className="divide-y" style={{ borderColor: "#E3D6C0" }}>
      {filled.map((item) => (
        <div key={item.id} className="grid grid-cols-[minmax(100px,0.7fr)_minmax(0,2fr)] gap-5 py-4">
          <div className="font-serif text-[18px]" style={{ color: TEXT }}>{item.term}</div>
          <div className="font-serif text-[14px] leading-[1.6]" style={{ color: MUTED }}>{item.text}</div>
        </div>
      ))}
    </div>
  );
}

function FaqPreview({ items }) {
  const filled = items.filter((x) => x.question.trim() || x.answer.trim());
  if (!filled.length) return null;
  return (
    <div className="space-y-2">
      {filled.map((item) => (
        <details key={item.id} className="group border" style={{ borderColor: "#D8C7AA", background: "rgba(255,255,255,0.38)" }}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 font-serif text-[17px]"
            style={{ color: TEXT }}>
            <span>{item.question}</span>
            <span className="text-xl leading-none transition group-open:rotate-45" style={{ color: ACCENT }}>+</span>
          </summary>
          {item.answer && <div className="px-4 pb-4 font-serif text-[14px] leading-[1.65]" style={{ color: MUTED }}>{item.answer}</div>}
        </details>
      ))}
    </div>
  );
}

function KnowledgePreview({ mode, data, mobile }) {
  if (mode === "table") return <TablePreview table={data.table} mobile={mobile} />;
  if (mode === "comparison") return <ComparisonPreview items={data.comparison} mobile={mobile} />;
  if (mode === "scheme") return <SchemePreview items={data.scheme} mobile={mobile} />;
  if (mode === "scale") return <ScalePreview items={data.scale} />;
  if (mode === "matrix") return <MatrixPreview matrix={data.matrix} mobile={mobile} />;
  if (mode === "glossary") return <GlossaryPreview items={data.glossary} />;
  if (mode === "faq") return <FaqPreview items={data.faq} />;
  return null;
}

function RowActions({ index, length, onMove, onRemove }) {
  return (
    <div className="flex shrink-0 gap-1">
      <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}
        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▲</button>
      <button type="button" onClick={() => onMove(index, 1)} disabled={index === length - 1}
        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▼</button>
      <button type="button" onClick={() => onRemove(index)}
        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px]">×</button>
    </div>
  );
}

export function EditorialKnowledgeV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [title, setTitle] = useState(initial.title);
  const [subtitle, setSubtitle] = useState(initial.subtitle);
  const [mode, setMode] = useState(initial.mode);
  const [table, setTable] = useState(initial.table);
  const [comparison, setComparison] = useState(initial.comparison);
  const [scheme, setScheme] = useState(initial.scheme);
  const [scale, setScale] = useState(initial.scale);
  const [matrix, setMatrix] = useState(initial.matrix);
  const [glossary, setGlossary] = useState(initial.glossary);
  const [faq, setFaq] = useState(initial.faq);
  const [image, setImage] = useState(initial.image);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imageInput = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setEyebrow(next.eyebrow);
    setTitle(next.title);
    setSubtitle(next.subtitle);
    setMode(next.mode);
    setTable(next.table);
    setComparison(next.comparison);
    setScheme(next.scheme);
    setScale(next.scale);
    setMatrix(next.matrix);
    setGlossary(next.glossary);
    setFaq(next.faq);
    setImage(next.image);
    setLibraryOpen(false);
    setCropOpen(false);
    setSaveState("idle");
  }, [block.id]);
  const moveListItem = (setter, index, dir) => setter((rows) => {
    const j = index + dir;
    if (j < 0 || j >= rows.length) return rows;
    const next = [...rows];
    [next[index], next[j]] = [next[j], next[index]];
    return next;
  });

  const removeListItem = (setter, index, prefix, fields) => setter((rows) => {
    const next = rows.filter((_, idx) => idx !== index);
    return next.length ? next : [{ id: newId(prefix), ...Object.fromEntries(fields.map((f) => [f, ""])) }];
  });

  const updateListItem = (setter, index, field, value) => setter((rows) =>
    rows.map((row, idx) => idx === index ? { ...row, [field]: value } : row));

  const addTableColumn = () => setTable((t) => {
    const col = { id: newId("col"), title: "" };
    return { columns: [...t.columns, col], rows: t.rows.map((r) => ({ ...r, cells: [...r.cells, ""] })) };
  });
  const removeTableColumn = (idx) => setTable((t) => {
    if (t.columns.length <= 1) return t;
    return {
      columns: t.columns.filter((_, i) => i !== idx),
      rows: t.rows.map((r) => ({ ...r, cells: r.cells.filter((_, i) => i !== idx) })),
    };
  });
  const addTableRow = () => setTable((t) => ({
    ...t,
    rows: [...t.rows, { id: newId("row"), cells: t.columns.map(() => "") }],
  }));

  const addMatrixColumn = () => setMatrix((m) => ({
    columns: [...m.columns, { id: newId("mx-c"), title: "" }],
    rows: m.rows.map((r) => ({ ...r, cells: [...r.cells, ""] })),
  }));
  const removeMatrixColumn = (idx) => setMatrix((m) => {
    if (m.columns.length <= 1) return m;
    return {
      columns: m.columns.filter((_, i) => i !== idx),
      rows: m.rows.map((r) => ({ ...r, cells: r.cells.filter((_, i) => i !== idx) })),
    };
  });

  const data = { table, comparison, scheme, scale, matrix, glossary, faq };

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    setImage(normalizeImage({ media_id: media.id, url }));
    setLibraryOpen(false);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setImage(normalizeImage({ media_id: data.id, url }));
      toast.success("Фото загружено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const knowledge = {
        eyebrow,
        title,
        subtitle,
        mode,
        table,
        comparison: { items: comparison },
        scheme: { items: scheme },
        scale: { items: scale },
        matrix,
        glossary: { items: glossary },
        faq: { items: faq },
        image: image ? { ...image } : null,
      };
      const prevExtra = c.extra && typeof c.extra === "object" ? c.extra : {};
      const nextContent = {
        ...c,
        eyebrow,
        headline: title,
        lead: subtitle,
        table: mode === "table" ? table : c.table,
        media_ids: image?.media_id ? [image.media_id] : [],
        extra: { ...prevExtra, knowledge },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      onSaved?.({ ...block, content: nextContent }, image?.media_id && image?.url ? [{ id: image.media_id, url: image.url }] : []);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = { idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения" }[saveState];

  const renderPairEditor = (items, setter, leftField, rightField, leftPlaceholder, rightPlaceholder, prefix) => (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={item.id} className="flex gap-2 rounded-lg border border-espresso/15 p-2">
          <div className="grid flex-1 gap-2 sm:grid-cols-[0.8fr_1.4fr]">
            <input value={item[leftField]} onChange={(e) => updateListItem(setter, i, leftField, e.target.value)}
              placeholder={leftPlaceholder} className="rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
            <textarea value={item[rightField]} onChange={(e) => updateListItem(setter, i, rightField, e.target.value)}
              rows={2} placeholder={rightPlaceholder} className="rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
          </div>
          <RowActions index={i} length={items.length}
            onMove={(idx, dir) => moveListItem(setter, idx, dir)}
            onRemove={(idx) => removeListItem(setter, idx, prefix, [leftField, rightField])} />
        </div>
      ))}
    </div>
  );

  return (
    <div onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between border-b border-espresso/10 px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Знание</div>
            <div className="mt-0.5 font-serif text-xl text-espresso">{title || "Знание"}</div>
          </div>
          {saveLabel && <span className="text-[11px] uppercase tracking-widest text-espresso/50">{saveLabel}</span>}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          <div className="space-y-3">
            <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
              placeholder="Метка раздела" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Заголовок" className="w-full rounded-lg border border-espresso/20 px-3 py-2 font-serif text-lg" />
            <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2}
              placeholder="Короткое пояснение · необязательно"
              className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
          </div>

          <div>
            <div className="mb-2 text-[11px] text-espresso/60">Фото · необязательно</div>
            {image?.url ? (
              <div className="flex items-start gap-3">
                <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
                  <img src={resolveMediaUrl(image.url)} alt={image.alt || ""} className="h-full w-full" style={editorialImageStyle(image)} />
                </div>
                <div className="flex-1 space-y-2">
                  <input value={image.alt} onChange={(e) => setImage((img) => img ? { ...img, alt: e.target.value } : img)}
                    placeholder="Описание изображения (alt)"
                    className="w-full rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                  <input value={image.caption} onChange={(e) => setImage((img) => img ? { ...img, caption: e.target.value } : img)}
                    placeholder="Подпись под изображением · необязательно"
                    className="w-full rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => imageInput.current?.click()} disabled={uploading}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50">
                      {uploading ? "Загрузка…" : "Заменить фото"}
                    </button>
                    <button type="button" onClick={() => setLibraryOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso">Из Медиатеки</button>
                    <button type="button" onClick={() => setCropOpen(true)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso">Настроить кадр</button>
                    <button type="button" onClick={() => setImage(null)}
                      className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70">Удалить фото</button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => imageInput.current?.click()} disabled={uploading}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50">
                  {uploading ? "Загрузка…" : "Добавить фото"}
                </button>
                <button type="button" onClick={() => setLibraryOpen(true)}
                  className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso">Из Медиатеки</button>
              </div>
            )}
            <input ref={imageInput} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; uploadImage(file); }} />
          </div>

          <div>
            <div className="mb-2 text-[11px] text-espresso/60">Формат знания</div>
            <div className="flex flex-wrap gap-2">
              {MODES.map((item) => (
                <button key={item.key} type="button" onClick={() => setMode(item.key)}
                  className={mode === item.key
                    ? "rounded-xl border border-espresso bg-espresso px-3 py-2 text-xs text-cream"
                    : "rounded-xl border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"}>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {mode === "table" && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-[11px] text-espresso/60">Таблица</div>
                <div className="flex gap-2">
                  <button type="button" onClick={addTableColumn} className="text-xs text-espresso/60 underline">+ Столбец</button>
                  <button type="button" onClick={addTableRow} className="text-xs text-espresso/60 underline">+ Строка</button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-espresso/15 bg-white/60 p-2">
                <div className="min-w-[540px] space-y-2">
                  <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${table.columns.length}, minmax(120px,1fr)) 34px` }}>
                    {table.columns.map((col, i) => (
                      <div key={col.id} className="flex gap-1">
                        <input value={col.title} onChange={(e) => setTable((t) => ({
                          ...t, columns: t.columns.map((c2, idx) => idx === i ? { ...c2, title: e.target.value } : c2),
                        }))} placeholder="Заголовок"
                          className="min-w-0 flex-1 rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                        {table.columns.length > 1 && <button type="button" onClick={() => removeTableColumn(i)} className="text-xs text-espresso/40">×</button>}
                      </div>
                    ))}
                    <div />
                  </div>
                  {table.rows.map((row, rIdx) => (
                    <div key={row.id} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${table.columns.length}, minmax(120px,1fr)) 34px` }}>
                      {table.columns.map((col, cIdx) => (
                        <input key={col.id} value={row.cells[cIdx] || ""}
                          onChange={(e) => setTable((t) => ({
                            ...t,
                            rows: t.rows.map((r, idx) => idx === rIdx ? { ...r, cells: r.cells.map((v, j) => j === cIdx ? e.target.value : v) } : r),
                          }))}
                          placeholder="Значение" className="rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                      ))}
                      <button type="button" onClick={() => setTable((t) => ({
                        ...t, rows: t.rows.length > 1 ? t.rows.filter((_, idx) => idx !== rIdx) : t.rows,
                      }))} className="text-xs text-espresso/40">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === "comparison" && (
            <div>
              <div className="mb-2 flex justify-between"><div className="text-[11px] text-espresso/60">Сравнение</div>
                <button type="button" onClick={() => setComparison((rows) => [...rows, { id: newId("cmp"), title: "", text: "" }])}
                  className="text-xs text-espresso/60 underline">+ Добавить</button></div>
              {renderPairEditor(comparison, setComparison, "title", "text", "Название", "Описание / отличие", "cmp")}
            </div>
          )}

          {mode === "scheme" && (
            <div>
              <div className="mb-2 flex justify-between"><div className="text-[11px] text-espresso/60">Схема / алгоритм</div>
                <button type="button" onClick={() => setScheme((rows) => [...rows, { id: newId("sch"), title: "", text: "" }])}
                  className="text-xs text-espresso/60 underline">+ Добавить узел</button></div>
              {renderPairEditor(scheme, setScheme, "title", "text", "Шаг / условие", "Пояснение", "sch")}
            </div>
          )}

          {mode === "scale" && (
            <div>
              <div className="mb-2 flex justify-between"><div className="text-[11px] text-espresso/60">Шкала / диапазон</div>
                <button type="button" onClick={() => setScale((rows) => [...rows, { id: newId("scl"), label: "", text: "" }])}
                  className="text-xs text-espresso/60 underline">+ Добавить отметку</button></div>
              {renderPairEditor(scale, setScale, "label", "text", "Значение / диапазон", "Что означает", "scl")}
            </div>
          )}

          {mode === "matrix" && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-[11px] text-espresso/60">Матрица</div>
                <div className="flex gap-2">
                  <button type="button" onClick={addMatrixColumn} className="text-xs text-espresso/60 underline">+ Столбец</button>
                  <button type="button" onClick={() => setMatrix((m) => ({
                    ...m, rows: [...m.rows, { id: newId("mx-r"), label: "", cells: m.columns.map(() => "") }],
                  }))} className="text-xs text-espresso/60 underline">+ Строка</button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-espresso/15 bg-white/60 p-2">
                <div className="min-w-[620px] space-y-2">
                  <div className="grid gap-2" style={{ gridTemplateColumns: `140px repeat(${matrix.columns.length}, minmax(120px,1fr)) 34px` }}>
                    <div className="text-[10px] uppercase tracking-widest text-espresso/45">Объект</div>
                    {matrix.columns.map((col, i) => (
                      <div key={col.id} className="flex gap-1">
                        <input value={col.title} onChange={(e) => setMatrix((m) => ({
                          ...m, columns: m.columns.map((c2, idx) => idx === i ? { ...c2, title: e.target.value } : c2),
                        }))} placeholder="Параметр" className="min-w-0 flex-1 rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                        {matrix.columns.length > 1 && <button type="button" onClick={() => removeMatrixColumn(i)} className="text-xs text-espresso/40">×</button>}
                      </div>
                    ))}
                    <div />
                  </div>
                  {matrix.rows.map((row, rIdx) => (
                    <div key={row.id} className="grid gap-2" style={{ gridTemplateColumns: `140px repeat(${matrix.columns.length}, minmax(120px,1fr)) 34px` }}>
                      <input value={row.label} onChange={(e) => setMatrix((m) => ({
                        ...m, rows: m.rows.map((r, idx) => idx === rIdx ? { ...r, label: e.target.value } : r),
                      }))} placeholder="Название" className="rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                      {matrix.columns.map((col, cIdx) => (
                        <input key={col.id} value={row.cells[cIdx] || ""} onChange={(e) => setMatrix((m) => ({
                          ...m,
                          rows: m.rows.map((r, idx) => idx === rIdx ? {
                            ...r,
                            cells: m.columns.map((_, j) => j === cIdx ? e.target.value : (r.cells[j] || "")),
                          } : r),
                        }))} placeholder="Значение" className="rounded-lg border border-espresso/20 px-2 py-1.5 text-xs" />
                      ))}
                      <button type="button" onClick={() => setMatrix((m) => ({
                        ...m, rows: m.rows.length > 1 ? m.rows.filter((_, idx) => idx !== rIdx) : m.rows,
                      }))} className="text-xs text-espresso/40">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === "glossary" && (
            <div>
              <div className="mb-2 flex justify-between"><div className="text-[11px] text-espresso/60">Справочник / термины</div>
                <button type="button" onClick={() => setGlossary((rows) => [...rows, { id: newId("gl"), term: "", text: "" }])}
                  className="text-xs text-espresso/60 underline">+ Добавить термин</button></div>
              {renderPairEditor(glossary, setGlossary, "term", "text", "Термин", "Определение", "gl")}
            </div>
          )}

          {mode === "faq" && (
            <div>
              <div className="mb-2 flex justify-between"><div className="text-[11px] text-espresso/60">FAQ / гармошка</div>
                <button type="button" onClick={() => setFaq((rows) => [...rows, { id: newId("faq"), question: "", answer: "" }])}
                  className="text-xs text-espresso/60 underline">+ Добавить вопрос</button></div>
              {renderPairEditor(faq, setFaq, "question", "answer", "Вопрос", "Ответ", "faq")}
            </div>
          )}

          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={previewDevice === "desktop" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>Desktop</button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={previewDevice === "mobile" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>Mobile</button>
                </div>
              </div>
              <div className={previewDevice === "mobile" ? "mx-auto max-w-[280px] p-5" : "mx-auto max-w-[980px] p-7"}
                style={{ backgroundColor: BG }}>
                <SectionHeader eyebrow={eyebrow} title={title} subtitle={subtitle} />
                {image?.url && (
                  <figure className="mb-7">
                    <div className={previewDevice === "mobile" ? "mx-auto max-w-[220px]" : "mx-auto max-w-[420px]"}>
                      <div className="aspect-[4/5] overflow-hidden bg-[#E7DDCC]">
                        <img src={resolveMediaUrl(image.url)} alt={image.alt || ""} className="h-full w-full" style={editorialImageStyle(image)} />
                      </div>
                      {image.caption && <figcaption className="mt-2 font-serif text-[12px] italic leading-[1.5]" style={{ color: MUTED }}>{image.caption}</figcaption>}
                    </div>
                  </figure>
                )}
                <KnowledgePreview mode={mode} data={data} mobile={previewDevice === "mobile"} />
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">Закрыть</button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50">Сохранить изменения</button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={libraryOpen} onClose={() => setLibraryOpen(false)} onPick={pickFromLibrary} />
      <EditorialCropV2
        open={cropOpen}
        url={resolveMediaUrl(image?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel={FRAME_LABEL}
        focalX={image?.focal_x ?? 0.5}
        focalY={image?.focal_y ?? 0.5}
        zoom={image?.zoom ?? 1}
        onSave={(values) => { setImage((img) => img ? { ...img, ...values } : img); setCropOpen(false); }}
        onClose={() => setCropOpen(false)}
      />
    </div>
  );
}
